import {
  AccountStatusEnum,
  InternalAdvanceAccount,
  InternalChargeCardAccount,
  InternalCheckingAccount,
  InternalLineOfCreditAccount,
  InternalPersonalLoanAccount,
  InternalSavingsAccount,
  Query,
  VaroMoneyAccount,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import fetchAccountsStatements from 'services/graphql/queries/FetchAccountsStatements';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useFormattedCombinedError } from 'shared/hooks/errorFormatters/useFormattedCombinedError/useFormattedCombinedError';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { RequestPolicy, useQuery } from 'urql';

interface AccountStatements {
  accountId?: string;
  year?: string;
  month?: string;
  pause?: boolean;
  requestPolicy?: RequestPolicy;
}

const useAccountStatements = ({
  accountId = '',
  year = '',
  month = '',
  pause = false,
  requestPolicy = 'cache-first',
}: AccountStatements) => {
  const router = useRouter();
  const [
    {
      data: accountStatementsData,
      fetching: accountStatementsLoading,
      error: accountStatementsError,
    },
    refetchAccountStatements,
  ] = useQuery<Query>({
    pause,
    query: fetchAccountsStatements,
    requestPolicy,
  });

  const refresh = () => {
    refetchAccountStatements({ requestPolicy: 'network-only' });
  };

  const cancel = () => {
    router.back();
  };

  const { defaultTitle, defaultMessage, retryButtonTitle, cancelButtonTitle } =
    useDefaultErrorStrings();

  const responseError = useFormattedCombinedError(accountStatementsError);

  const buttons = {
    primaryButton: {
      title: retryButtonTitle,
      onPress: refresh,
    },
    secondaryButton: {
      title: cancelButtonTitle,
      onPress: cancel,
    },
  };

  const errorModel = accountStatementsError
    ? {
        screenType: ErrorScreenType.Error,
        messageHeadline: responseError?.title ?? defaultTitle,
        messageBody: responseError?.message ?? defaultMessage,
        ...buttons,
      }
    : null;

  const accountStatementsList =
    accountStatementsData?.myAccounts?.forStatements;

  const accountStatementsDetails = accountStatementsList?.flatMap(
    (
      account:
        | InternalLineOfCreditAccount
        | InternalSavingsAccount
        | InternalCheckingAccount
        | InternalAdvanceAccount
        | InternalChargeCardAccount
        | InternalPersonalLoanAccount
        | VaroMoneyAccount,
    ) => {
      return account?.status === AccountStatusEnum.Active &&
        account.monthlyStatements
        ? account
        : [];
    },
  );

  const closedStatementsDetails = accountStatementsList?.flatMap(
    (
      account:
        | InternalLineOfCreditAccount
        | InternalSavingsAccount
        | InternalCheckingAccount
        | InternalAdvanceAccount
        | InternalChargeCardAccount
        | InternalPersonalLoanAccount
        | VaroMoneyAccount,
    ) => {
      if (
        account?.status === AccountStatusEnum.Closed &&
        account.monthlyStatements
      ) {
        return account;
      }
      return [];
    },
  );

  const yearlyStatementsData = [
    ...(accountStatementsDetails?.flatMap((account) =>
      account?.id === accountId ? account.monthlyStatements : [],
    ) || []),
    ...(closedStatementsDetails?.flatMap((account) =>
      account?.id === accountId ? account.monthlyStatements : [],
    ) || []),
  ];

  const monthlyStatementsData = yearlyStatementsData?.flatMap((statements) => {
    return year === statements?.year?.toString() ? statements.groups : [];
  });

  const selectedMonthStatementsData = monthlyStatementsData?.flatMap(
    (statements) => {
      return month === statements?.title?.toLowerCase()
        ? statements.statements
        : [];
    },
  );

  return {
    accountStatementsDetails,
    yearlyStatementsData,
    monthlyStatementsData,
    selectedMonthStatementsData,
    closedStatementsDetails,
    errorModel,
    loading: accountStatementsLoading,
    refresh,
  };
};

export default useAccountStatements;
